import { DocumentRecordsListSortBy, DocumentRecordsSortDirection, StrategyDetailsPageGetDocumentRecordQuery } from '@/__generated__/graphql';
import client from '@/apollo-client';
import { EditIcon } from '@/assets/icons/edit';
import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import Button from '@/components/basicComponents/button';
import FileUpload from '@/components/basicComponents/fileUpload';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import Tag from '@/components/basicComponents/tag';
import { IOfferingMaterialsAttachments } from '@/components/dealCatalog-page/types';
import { isImageExtension } from '@/components/dueDiligence-page/dueDiligenceRecord/utils';
import { Loader } from '@/components/loaders/loader';
import { GET_ASSET_SIGNED_URL_QUERY } from '@/components/settings-page/queries';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { IFieldEditor } from '@/shared/types/fieldEditor';
import { convertDocumentRecordFieldToFieldEditor, FieldList, shouldDisplayField } from '@/shared/ui/fieldList';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { NetworkStatus, useQuery } from '@apollo/client';
import { DocumentRecordStatus, TenantType } from '@trace-8/trace8-schema';
import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { NavigateOptions, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { MANAGER_UPDATES_EDITABLE_ROLES } from '../../../managerUpdates-page/constants';
import { getTagStyleType } from '../../../managerUpdates-page/utils';
import AddUpdate from './modals/addUpdate';
import DownloadPDF from './modals/downloadPDF';
import { STRATEGY_DETAILS_PAGE_GET_DOCUMENT_RECORD_QUERY, STRATEGY_DETAILS_PAGE_LIST_DOCUMENT_RECORDS_NAMES_QUERY } from './queries';

interface ISectionAnchorNavItem {
  id: string;
  title: string;
  icon?: JSX.Element;
}

const shouldDisplayManagerUpdatePreviewField = (row: StrategyDetailsPageGetDocumentRecordQuery['getDocumentRecord']['fields'][number]) => {
  const { value, commentary, AI } = row;
  return (!!AI?.answers?.length && value == null && commentary == null) || !!value || !!commentary;
};

interface UpdatesTabProps {
  managerId: string | null;
  offeringMaterialsAttachments: IOfferingMaterialsAttachments[];
  backToTitle: string;
}

export const UpdatesTab = ({ managerId, offeringMaterialsAttachments, backToTitle }: UpdatesTabProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const params = useParams();
  const { isMobile, isTablet } = useResponsive();

  const [searchParams, setSearchParams] = useSearchParams();

  const documentRecordId = searchParams.get('documentRecordId') || '';

  const [canUserEdit, setCanUserEdit] = useState(false);
  const [selectedDocumentRecord, setSelectedDocumentRecord] = useState<StrategyDetailsPageGetDocumentRecordQuery['getDocumentRecord'] | null>(null);
  const [allFields, setAllFields] = useState<IFieldEditor[]>([]);
  const [documentRecordSelectOptions, setDocumentRecordSelectOptions] = useState<ISelectValue[]>([]);
  const [sectionAnchorNavItems, setSectionAnchorNavItems] = useState<ISectionAnchorNavItem[]>([]);
  const [activeSection, setActiveSection] = useState('');
  const [navHeightState, setNavHeightState] = useState(70);
  const [showZeroState, setShowZeroState] = useState(false);
  const [hasManagerUpdateContent, setHasManagerUpdateContent] = useState(false);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'add-update' });
  const [visibleAttachmentsForAdvisors, setVisibleAttachmentsForAdvisors] = useState<IOfferingMaterialsAttachments[]>([]);

  const navRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (navRef.current) {
      setNavHeightState(isMobile || isTablet ? navRef.current.offsetHeight + 50 : navRef.current.offsetHeight + 5);
    }
  }, []);

  useEffect(() => {
    if (MANAGER_UPDATES_EDITABLE_ROLES.some((role) => user.roles.includes(role)) && user.tenant.type === TenantType.PLATFORM_MANAGER) {
      setCanUserEdit(true);
      return;
    }
  }, [user]);

  useEffect(() => {
    if (!offeringMaterialsAttachments?.length || !documentRecordId) return;

    const currentDocumentRecordAssetKey = `investments/${params?.strategyId}/mu/${documentRecordId}`;
    const filteredAttachments =
      offeringMaterialsAttachments?.filter(
        (attachment) => attachment?.asset?.assetKey?.includes(currentDocumentRecordAssetKey) && attachment.visibility === 'Visible to Advisors'
      ) || [];

    setVisibleAttachmentsForAdvisors(filteredAttachments);
  }, [offeringMaterialsAttachments, documentRecordId]);

  const { networkStatus: documentRecordNetworkStatus } = useQuery(STRATEGY_DETAILS_PAGE_GET_DOCUMENT_RECORD_QUERY, {
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
    skip: !documentRecordId,
    variables: {
      id: documentRecordId
    },
    onCompleted: (data) => {
      const { fields } = data?.getDocumentRecord || {};

      const filteredManagerUpdateFields = fields?.filter(
        (field) => field.documentField.name !== 'Document Record Name' && field.documentField.name !== 'Document Record Publish Date'
      );
      const updatedDocumentRecordData = {
        ...data.getDocumentRecord,
        fields: filteredManagerUpdateFields
      };
      setSelectedDocumentRecord(updatedDocumentRecordData);

      const allEditFields: IFieldEditor[] = convertDocumentRecordFieldToFieldEditor(filteredManagerUpdateFields)?.filter((field) =>
        shouldDisplayField(field)
      );
      setAllFields(allEditFields);

      const sectionNavItems: ISectionAnchorNavItem[] = filteredManagerUpdateFields
        .filter((field) => shouldDisplayManagerUpdatePreviewField(field))
        .map((field) => ({
          id: field.documentField.name,
          title: field.documentField.name
        }));
      setActiveSection(sectionNavItems?.[0]?.id || '');
      setSectionAnchorNavItems(sectionNavItems);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { loading: listDocumentRecordsLoading } = useQuery(STRATEGY_DETAILS_PAGE_LIST_DOCUMENT_RECORDS_NAMES_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !params?.strategyId,
    variables: {
      data: {
        investmentId: params?.strategyId,
        sortBy: DocumentRecordsListSortBy.StatusUpdatedDate,
        sortDirection: DocumentRecordsSortDirection.Desc,
        documentTypeFilter: ['mu'],
        limit: 1000
      },
      fieldNames: ['Document Record Name']
    },
    onCompleted: (data) => {
      const { total, records } = data.listDocumentRecords || {};

      setShowZeroState(!total);

      const selectOptions = records.map((record) => {
        const { id, name, status, statusUpdatedAt, documentRecordFieldAnswers } = record;
        const statusLabel = DocumentRecordStatus[status];
        const isPublished = status === getEnumKeyByValue(DocumentRecordStatus, DocumentRecordStatus.Published);

        const basicInfoNameField = documentRecordFieldAnswers?.find((field) => field.documentField.name === 'Document Record Name');

        const baseName =
          basicInfoNameField && basicInfoNameField.value === null && basicInfoNameField.AI?.answers?.length ? basicInfoNameField.AI.answers[0] : name;

        let label = `${baseName} (${statusLabel}`;

        if (isPublished && statusUpdatedAt) {
          const formattedDate = format(new Date(statusUpdatedAt), 'MMM d, yyyy');
          label += ` ${formattedDate}`;
        }

        label += ')';

        return {
          value: id,
          label
        };
      });

      setDocumentRecordSelectOptions(selectOptions);

      const isDocumentRecordIdExists = selectOptions.some((option) => option.value === documentRecordId);

      if (!isDocumentRecordIdExists) {
        const lastPublishedRecord = records.find((record) => record.status === DocumentRecordStatus.Published);
        const lastDraftRecord = records.find((record) => record.status === DocumentRecordStatus.Draft);
        const lastAvailableRecord = lastPublishedRecord ?? lastDraftRecord;

        onChangeDocumentRecord(lastAvailableRecord?.id || '');
      }
    },
    onError: (error) => {
      console.error(error);
    }
  });

  useEffect(() => {
    const hasFieldsContent = selectedDocumentRecord?.fields?.some((field) => shouldDisplayManagerUpdatePreviewField(field));
    setHasManagerUpdateContent(hasFieldsContent || !!visibleAttachmentsForAdvisors?.length);
  }, [selectedDocumentRecord, visibleAttachmentsForAdvisors]);

  const openPage = (path: string, options?: NavigateOptions) => {
    navigate(path, options);
  };

  const openModalWindow = (type: string) => {
    setModalWindow({ ...modalWindow, isOpen: true, type });
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
  };

  const openAttachment = async (assetKey: string) => {
    try {
      const { data } = await client.query({
        fetchPolicy: 'network-only',
        query: GET_ASSET_SIGNED_URL_QUERY,
        variables: {
          assetKey
        }
      });
      window.open(`${data.getAssetSignedUrl}`, '_blank');
    } catch (error) {
      console.error(error);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    const lastFieldName = selectedDocumentRecord?.fields?.[selectedDocumentRecord?.fields?.length - 1]?.documentField?.name;
    setActiveSection(id === 'viewManagerUpdateAttachments' ? lastFieldName : id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - navHeightState,
        behavior: 'smooth'
      });
    }
  };

  const onChangeDocumentRecord = (id: string) => {
    setSearchParams(
      (prev) => {
        prev.set('documentRecordId', id);
        return prev;
      },
      { replace: true }
    );
  };

  if (listDocumentRecordsLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <CustomPaddingWrap>
      {modalWindow.isOpen && modalWindow.type === 'add-update' && (
        <AddUpdate
          onClose={closeModalWindow}
          isOpen={modalWindow.isOpen}
          manager={managerId}
          openEditManagerUpdatePage={(updateId, options) => openPage(`allUpdates/edit/${updateId}?backToTitle=${backToTitle}`, options)}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'download-pdf' && (
        <DownloadPDF isOpen={modalWindow.isOpen} onClose={closeModalWindow} updateId={selectedDocumentRecord?.id} source="updateTab" />
      )}
      {showZeroState ? (
        <NoUpdates>
          <div>No manager updates have been logged for this investment.</div>
          {canUserEdit && (
            <Button size="md" onClick={() => openModalWindow('add-update')} icon={<AddButtonIcon />}>
              Add New Update
            </Button>
          )}
        </NoUpdates>
      ) : (
        <>
          <SelectedUpdateControlWrapper>
            <SelectedUpdateControl>
              <SelectedUpdateLabel>Selected Update</SelectedUpdateLabel>
              <Select
                value={documentRecordId}
                setValue={onChangeDocumentRecord}
                options={documentRecordSelectOptions}
                size="sm"
                width={isMobile || isTablet ? '250px' : '520px'}
                {...(canUserEdit && {
                  additionalOption: (
                    <EditManagerUpdatesButton onClick={() => openPage(`allUpdates?backToTitle=${backToTitle}`)}>
                      <EditIcon width={20} height={19} fill={themeVar('font.action')} />
                      Edit Manager Updates
                    </EditManagerUpdatesButton>
                  )
                })}
              />
              {selectedDocumentRecord?.sentimentFlag && selectedDocumentRecord?.sentimentFlagLabel && (
                <Tag
                  title={selectedDocumentRecord?.sentimentFlagLabel}
                  size="sm"
                  styleType={getTagStyleType(selectedDocumentRecord?.sentimentFlag)}
                />
              )}
            </SelectedUpdateControl>
            {canUserEdit && (
              <SelectedUpdateControlButtons>
                <Button
                  styleType="outline"
                  size="md"
                  onClick={() => openPage(`allUpdates/edit/${selectedDocumentRecord?.id}?backToTitle=${backToTitle}`)}
                >
                  Edit Update
                </Button>
                <Button styleType="outline" size="md" onClick={() => openModalWindow('add-update')}>
                  Add New Update
                </Button>
              </SelectedUpdateControlButtons>
            )}
          </SelectedUpdateControlWrapper>
          {documentRecordNetworkStatus === NetworkStatus.loading || documentRecordNetworkStatus === NetworkStatus.setVariables ? (
            <LoaderWrapper>
              <Loader />
            </LoaderWrapper>
          ) : (
            <>
              {!!sectionAnchorNavItems?.length && (
                <SectionsNavWrapper isMobile={isMobile} isTablet={isTablet} ref={navRef}>
                  <SectionAnchorNavWrapper isMobile={isMobile} isTablet={isTablet}>
                    {sectionAnchorNavItems.map((item) => (
                      <SectionAnchorNavItem key={item.id} isActive={activeSection === item.id} onClick={() => scrollToSection(item.id)}>
                        {item.title}
                      </SectionAnchorNavItem>
                    ))}
                  </SectionAnchorNavWrapper>
                  <AttachmentsButton
                    styleType="outline"
                    size="sm"
                    onClick={() => scrollToSection('viewManagerUpdateAttachments')}
                    icon={<AttachmentsCount id="attachmentsCount">{visibleAttachmentsForAdvisors?.length || 0} </AttachmentsCount>}
                  >
                    Attachments
                  </AttachmentsButton>
                </SectionsNavWrapper>
              )}
              {hasManagerUpdateContent ? (
                <SectionsWrapper>
                  <FieldList fields={allFields} mode="view" />
                  {!!visibleAttachmentsForAdvisors.length && (
                    <PreviewWrapper id="viewManagerUpdateAttachments">
                      {visibleAttachmentsForAdvisors.map((attachment) => (
                        <FileUpload
                          key={attachment.asset.assetKey}
                          type={isImageExtension(attachment.asset.assetKey) ? 'image' : 'document'}
                          imageSrc={attachment.asset.url}
                          fileName={attachment.asset.assetKey.split('/').pop()}
                          openFile={() => openAttachment(attachment.asset.assetKey)}
                        />
                      ))}
                    </PreviewWrapper>
                  )}
                  <DownloadButtonWrapper>
                    <Button size="md" styleType="outline" onClick={() => openModalWindow('download-pdf')}>
                      Download
                    </Button>
                  </DownloadButtonWrapper>
                </SectionsWrapper>
              ) : (
                <NoManagerUpdateContent>
                  <NoManagerUpdateContentImageWrapper>
                    <NoManagerUpdateContentImage src="/static/modifyStrategy-page/descriptionZeroState.webp" draggable="false" alt="No Investors" />
                  </NoManagerUpdateContentImageWrapper>
                  <NoManagerUpdateContentTextBlock>
                    <NoManagerUpdateContentText>This Manager Update has no content.</NoManagerUpdateContentText>
                    {canUserEdit && (
                      <Button
                        onClick={() => openPage(`allUpdates/edit/${selectedDocumentRecord?.id}?backToTitle=${backToTitle}`)}
                        styleType="outline"
                        size="md"
                        width="max-content"
                      >
                        Edit Update
                      </Button>
                    )}
                  </NoManagerUpdateContentTextBlock>
                </NoManagerUpdateContent>
              )}
            </>
          )}
        </>
      )}
    </CustomPaddingWrap>
  );
};

const CustomPaddingWrap = styled(PaddingWrap)`
  padding-bottom: 40px;
`;

const LoaderWrapper = styled.div`
  margin-top: 50px;
`;

const NoUpdates = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 400;
  font-size: 16px;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
  gap: 40px;
  margin-top: 40px;
`;

const SelectedUpdateControlWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 15px 0 20px;
`;

const SelectedUpdateControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const SelectedUpdateControlButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  white-space: nowrap;
`;

const SelectedUpdateLabel = styled.p`
  font-size: 16px;
  font-weight: 700;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
  padding: 10px 20px;
`;

const EditManagerUpdatesButton = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 13px;
  font-weight: 600;
  line-height: 18.2px;
  color: ${themeVar('font.action')};
`;

const SectionsNavWrapper = styled.div<{ isMobile: boolean; isTablet: boolean }>`
  display: flex;
  gap: 20px;
  padding: 10px 0;
  position: sticky;
  top: ${({ isMobile, isTablet }) => (isMobile || isTablet ? '50px' : '0')};
  z-index: 99;
  background-color: ${themeVar('layer.base')};
`;

const SectionAnchorNavWrapper = styled.div<{ isMobile: boolean; isTablet: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 10px 20px 10px;
  border-right: 1px solid ${themeVar('border.base')};
  position: sticky;
  top: ${({ isMobile, isTablet }) => (isMobile || isTablet ? '50px' : '0')};
  z-index: 99;
  background-color: ${themeVar('layer.base')};
`;

const SectionAnchorNavItem = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  color: ${({ isActive }) => (isActive ? themeVar('font.base') : themeVar('font.action'))};
  cursor: pointer;
  border-bottom: ${({ isActive }) => (isActive ? `4px solid ${themeVar('menu.active')}` : '4px solid transparent')};
  width: max-content;

  svg {
    path {
      fill: ${({ isActive }) => (isActive ? themeVar('font.base') : themeVar('font.action'))};
    }
  }
`;

const AttachmentsButton = styled(Button)`
  &:hover #attachmentsCount {
    color: ${themeVar('action.default')};
    background-color: ${themeVar('context.light')};
  }
`;

const AttachmentsCount = styled.span`
  color: ${themeVar('context.light')};
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  line-height: 18.2px;
  border-radius: 9px;
  padding: 0 8px;
  background: ${themeVar('action.default')};
`;

const SectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 40px;
  border-radius: 10px;
  border: 1px solid ${themeVar('border.base')};
  background: ${themeVar('layer.1')};
  margin-top: 10px;
`;

const DownloadButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 10px 0;
`;

const PreviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
`;

const NoManagerUpdateContent = styled.div`
  padding: 10px 40px;
  display: flex;
  align-items: center;
  gap: 30px;
  border-radius: 10px;
  background: ${themeVar('layer.1')};
  border: 1px solid ${themeVar('border.base')};
`;

const NoManagerUpdateContentImageWrapper = styled.div`
  max-width: 220px;
  max-height: 220px;
`;

const NoManagerUpdateContentImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const NoManagerUpdateContentTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const NoManagerUpdateContentText = styled.p`
  font-size: 28px;
  font-weight: 600;
  line-height: 22.4px;
  color: ${themeVar('font.weak')};
`;
